import { useEffect, useRef, useState } from 'react';
import { ButtonLink } from '../components/Shell';
import { prefersReducedMotion, useScrollProgress } from '../hooks';
import { useContent } from '../siteContent';
import { Badge, Screen, Sidebar, Topbar, type MockStatus } from '../mock/primitives';

type Row = { id: number; name: string; detail: string; status: MockStatus };
const queue: Omit<Row, 'id'>[] = [
  { name: 'ريم', detail: 'باقة الشهر · 200 جرام · وجبتان', status: 'pending' },
  { name: 'خالد', detail: 'باقة الأسبوعين · 150 جرام · وجبة', status: 'active' },
  { name: 'هيا', detail: 'باقة الشهر · 250 جرام · 3 وجبات', status: 'active' },
  { name: 'ماجد', detail: 'باقة الأسبوع · 200 جرام · وجبة', status: 'pending' },
];
const initialRows: Row[] = [
  { id: 1, name: 'سارة', detail: 'باقة الشهر · 200 جرام · وجبتان', status: 'active' },
  { id: 2, name: 'عبدالله', detail: 'باقة الأسبوعين · 250 جرام · وجبة', status: 'active' },
  { id: 3, name: 'نورة', detail: 'باقة الشهر · 150 جرام · 3 وجبات', status: 'frozen' },
  { id: 4, name: 'فهد', detail: 'باقة الأسبوع · 200 جرام · وجبة', status: 'pending' },
];

function DashboardMock() {
  const [rows, setRows] = useState(initialRows);
  const [meals, setMeals] = useState(86);
  const [active, setActive] = useState(214);
  const [pending, setPending] = useState(6);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (prefersReducedMotion() || !ref.current) return;
    let visible = false; let tick = 0;
    const observer = new IntersectionObserver(entries => { visible = entries[0]?.isIntersecting ?? false; });
    observer.observe(ref.current);
    const timer = window.setInterval(() => {
      if (!visible || document.hidden) return;
      tick += 1;
      if (tick % 2) { setMeals(value => value + 1 + (tick % 3)); return; }
      const next = queue[(tick / 2 - 1) % queue.length];
      setRows(current => [{ ...next, id: Date.now() }, ...current].slice(0, 4));
      if (next.status === 'pending') setPending(value => value + 1); else setActive(value => value + 1);
    }, 2600);
    return () => { window.clearInterval(timer); observer.disconnect(); };
  }, []);

  const metrics: [string, number][] = [['إجمالي العملاء', 268], ['اشتراكات فعالة', active], ['وجبات مصروفة اليوم', meals], ['بانتظار الموافقة', pending]];
  return <div ref={ref}><Screen label="لوحة تحكم ميلنكس، أرقام المشتركين والاشتراكات والوجبات المصروفة وأحدث الاشتراكات" className="mk-app">
    <Sidebar active="الرئيسية" />
    <div className="mk-main">
      <Topbar title="الرئيسية" />
      <div className="mk-content">
        <div className="mk-hero"><div><small>كل العمليات في مكان واحد</small><b>إدارة أسرع وخدمة أوضح</b></div><span className="mk-btn">صرف وجبة الآن</span></div>
        <div className="mk-metrics">{metrics.map(([label, value]) => <div className="mk-metric" key={label}><small>{label}</small><strong key={value} className="mk-tick">{value}</strong></div>)}</div>
        <div className="mk-grid2">
          <div className="mk-panel"><b className="mk-panel-title">أحدث الاشتراكات</b>{rows.map(row => <div className="mk-row mk-row-enter" key={row.id}><span><b>{row.name}</b><small>{row.detail}</small></span><Badge status={row.status} /></div>)}</div>
          <div className="mk-panel"><b className="mk-panel-title">تنبيهات تحتاج انتباهك</b>
            <div className="mk-alert"><i className="warn" /><b>9</b> اشتراكات تنتهي خلال 3 أيام</div>
            <div className="mk-alert"><i /><b key={pending} className="mk-tick">{pending}</b> طلبات تسجيل تنتظر الموافقة</div>
            <div className="mk-alert"><i className="teal" /><b key={meals} className="mk-tick">{meals}</b> وجبة صُرفت اليوم</div>
          </div>
        </div>
      </div>
    </div>
  </Screen></div>;
}

export function Hero() {
  const { hero, settings, system, journey, features } = useContent();
  const exploreTarget = system.visible ? '#system' : journey.visible ? '#journey' : features.visible ? '#features' : '#pricing';
  const stageRef = useRef<HTMLDivElement>(null);
  useScrollProgress(stageRef, { property: '--hp' });
  return <section className="mx-hero" aria-labelledby="hero-title">
    <div className="mx-container mx-hero-copy">
      <h1 id="hero-title" className="mx-display mx-intro"><span>{hero.line1}</span><span className="mx-green">{hero.line2}</span></h1>
      <p className="mx-lead mx-intro mx-intro-2">{hero.lead}</p>
      <div className="mx-actions mx-intro mx-intro-3">
        <ButtonLink href={settings.primaryCta} arrow>{hero.primaryCta}</ButtonLink>
        {hero.secondaryCta && <ButtonLink href={exploreTarget} variant="quiet">{hero.secondaryCta}</ButtonLink>}
      </div>
      {hero.note && <p className="mx-hero-note mx-intro mx-intro-3">{hero.note}</p>}
    </div>
    {hero.showDashboard ? <>
      <div ref={stageRef} className="mx-hero-stage mx-container">
        <div className="mx-hero-device mx-intro mx-intro-4"><DashboardMock /></div>
      </div>
      <span className="mx-sr">لوحة التحكم المعروضة توضيحية، والأسماء والأرقام فيها ليست بيانات حقيقية</span>
    </> : <div ref={stageRef} className="mx-hero-spacer" />}
  </section>;
}

